import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, FileEdit, CheckCircle, Send, Coins, AlertTriangle, ArrowRight, Download, Plus } from 'lucide-react';

interface StatusWorkflowInfoProps {
  defaultExpanded?: boolean;
}

const outgoingSteps = [
  {
    icon: <Plus className="w-4 h-4" />,
    label: 'Create / Upload',
    description: 'Created manually or uploaded for review before sending',
    color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
  },
  {
    icon: <FileEdit className="w-4 h-4" />,
    label: 'Ready to Process',
    description: 'Initial status, pending review',
    color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
  },
  {
    icon: <CheckCircle className="w-4 h-4" />,
    label: 'Confirmed',
    description: 'Reviewed and approved, ready to send',
    color: 'bg-coral-500/20 text-coral-600 dark:text-coral-400 border-coral-500/30',
  },
  {
    icon: <Send className="w-4 h-4" />,
    label: 'Sent',
    description: 'Invoice sent to client',
    color: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30',
  },
  {
    icon: <Coins className="w-4 h-4" />,
    label: 'Paid',
    description: 'Payment received from client',
    color: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
  },
];

const incomingSteps = [
  {
    icon: <Download className="w-4 h-4" />,
    label: 'Upload (Received)',
    description: 'Invoice received from a supplier or vendor',
    color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
  },
  {
    icon: <FileEdit className="w-4 h-4" />,
    label: 'Received',
    description: 'Logged and pending your review',
    color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
  },
  {
    icon: <CheckCircle className="w-4 h-4" />,
    label: 'Approved to Pay',
    description: 'Reviewed and approved, scheduled for payment',
    color: 'bg-coral-500/20 text-coral-600 dark:text-coral-400 border-coral-500/30',
  },
  {
    icon: <Coins className="w-4 h-4" />,
    label: 'Paid',
    description: 'You have paid this invoice',
    color: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
  },
];

const FlowRow: React.FC<{ steps: typeof outgoingSteps }> = ({ steps }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {steps.map((step, index) => (
      <React.Fragment key={step.label}>
        <div className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border ${step.color}`}>
          {step.icon}
          <span className="text-[11px] font-medium whitespace-nowrap">{step.label}</span>
        </div>
        {index < steps.length - 1 && (
          <ArrowRight className="w-3.5 h-3.5 text-coral-500/50 dark:text-foreground-muted flex-shrink-0" />
        )}
      </React.Fragment>
    ))}
  </div>
);

const StatusWorkflowInfo: React.FC<StatusWorkflowInfoProps> = ({ defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-coral-500/5 dark:bg-coral-500/5 border border-coral-500/20 dark:border-coral-500/15 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-coral-500/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-coral-500 dark:text-coral-400" />
          <span className="text-sm font-medium text-coral-700 dark:text-foreground-secondary">Invoice Status Workflow</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-coral-500 dark:text-coral-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-coral-500 dark:text-coral-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Outgoing path */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-coral-600 dark:text-foreground-muted uppercase tracking-wide">
              Outgoing — Invoices you send (money in)
            </p>
            <FlowRow steps={outgoingSteps} />
          </div>

          {/* Incoming path */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-blue-600 dark:text-blue-300 uppercase tracking-wide">
              Incoming — Invoices you received (money out)
            </p>
            <FlowRow steps={incomingSteps} />
          </div>

          {/* Overdue notice */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-red-600 dark:text-red-300">Overdue Status</p>
              {/* "Automatically marked" was only ever true for created invoices:
                  check_and_mark_overdue() lives on the Invoice model and is swept
                  over Invoice rows when the dashboard loads. Uploaded documents
                  have no such sweep — they are counted as overdue from their
                  extracted due date, and their stored status does not change. */}
              <p className="text-xs text-red-500/70 dark:text-red-200/70 mt-0.5">
                Anything past its due date and not yet paid counts as overdue —
                outgoing from <strong>Sent</strong>, incoming from <strong>Approved to Pay</strong>.
                Invoices you created move to the <strong>Overdue</strong> status; uploaded
                documents keep their status and are flagged from their due date.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusWorkflowInfo;
